import { useEffect, useMemo, useState } from "react";
import { Check, X, Search } from "lucide-react";

interface MarkerInlineEditorProps {
  /** Anchor box in image natural pixels — same as FieldEditPopover. */
  anchor: { x: number; y: number; width: number; height: number };
  imageNatural: { width: number; height: number };
  /** Current field name — pre-selected in the list. */
  currentName: string;
  /** Suggestion list from fields_attribute_identifier_list. */
  suggestions: string[];
  onConfirm: (selectedName: string) => void;
  onCancel: () => void;
}

/**
 * Compact list popover anchored to a marker on the document. Renders the
 * suggestions from `fields_attribute_identifier_list` directly — no name
 * input, no content input, just the list. Click an item to select it,
 * then OK / Cancel.
 */
export function MarkerInlineEditor({
  anchor,
  imageNatural,
  currentName,
  suggestions,
  onConfirm,
  onCancel,
}: MarkerInlineEditorProps) {
  const [selected, setSelected] = useState(currentName);
  const [filter, setFilter] = useState("");

  useEffect(() => setSelected(currentName), [currentName]);

  const list = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    // Keep the current name at the top so users always know what they're
    // editing and can leave it unchanged.
    if (currentName) {
      result.push(currentName);
      seen.add(currentName);
    }
    for (const s of suggestions) {
      if (!s || seen.has(s)) continue;
      seen.add(s);
      result.push(s);
    }
    if (!filter.trim()) return result;
    const q = filter.toLowerCase();
    return result.filter((s) => s.toLowerCase().includes(q));
  }, [suggestions, currentName, filter]);

  const naturalW = Math.max(imageNatural.width, 1);
  const naturalH = Math.max(imageNatural.height, 1);
  const gapPct = 1;
  const popoverWPct = 13;

  // Anchor: just below the marker key bbox.
  const leftPct = clamp(
    (anchor.x / naturalW) * 100,
    gapPct,
    100 - popoverWPct - gapPct,
  );
  const topPct = ((anchor.y + anchor.height) / naturalH) * 100 + gapPct;

  return (
    <div
      style={{
        position: "absolute",
        top: `${topPct}%`,
        left: `${leftPct}%`,
        width: `${popoverWPct}%`,
        minWidth: "11rem",
        maxWidth: "14rem",
      }}
      className="z-30 flex flex-col rounded-lg border border-gray-200 bg-white shadow-xl ring-1 ring-black/5"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Optional search — only shown when there are >5 items so the
          UI stays compact for short lists like the eroyal sample. */}
      {suggestions.length > 5 ? (
        <div className="border-b border-gray-100 px-2 py-1.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter…"
              className="w-full rounded-md border border-gray-200 bg-gray-50 py-1 pl-6 pr-2 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      ) : null}

      {/* Suggestion list */}
      <ul className="max-h-52 overflow-y-auto py-1">
        {list.length === 0 ? (
          <li className="px-2 py-1.5 text-xs text-gray-400">No suggestions</li>
        ) : (
          list.map((s) => {
            const isSelected = s === selected;
            return (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => setSelected(s)}
                  onDoubleClick={() => onConfirm(s)}
                  className={`flex w-full items-center justify-between gap-2 truncate px-2 py-1 text-left text-xs ${
                    isSelected
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  title={s}
                >
                  <span className="truncate">{s}</span>
                  {isSelected ? (
                    <Check className="h-3 w-3 flex-shrink-0 text-blue-600" />
                  ) : null}
                </button>
              </li>
            );
          })
        )}
      </ul>

      {/* Footer actions */}
      <div className="flex items-center justify-end gap-1 border-t border-gray-100 bg-gray-50/60 px-2 py-1.5">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 rounded-md bg-rose-500 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-rose-600"
        >
          <X className="h-3 w-3" />
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(selected)}
          disabled={!selected}
          className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50"
        >
          <Check className="h-3 w-3" />
          OK
        </button>
      </div>
    </div>
  );
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
